import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import {
  ArrowLeft, Loader2, ShieldCheck, Star, CheckCircle2, X as XIcon,
} from "lucide-react";

const DESIGN_TIERS = {
  basic:    { id: "basic",    nameKey: "pricingPage.tierBasic",    focusKey: "pricingPage.focusNaksha",        mrp: 5330,  price: 1599, validityKey: "pricingPage.validity30"  },
  standard: { id: "standard", nameKey: "pricingPage.tierStandard", focusKey: "pricingPage.focusArchitectural", mrp: 13330, price: 3999, validityKey: "pricingPage.validity90"  },
  popular:  { id: "popular",  nameKey: "pricingPage.tierPopular",  focusKey: "pricingPage.focusConstruction",  mrp: 23330, price: 6999, validityKey: "pricingPage.validity365", highlight: true },
  premium:  { id: "premium",  nameKey: "pricingPage.tierPremium",  focusKey: "pricingPage.focusStructural",    mrp: 33330, price: 9999, validityKey: "pricingPage.validity365" },
};

const FEATURE_KEYS = [
  { key: "pricingPage.f2d",       in: ["basic", "standard", "popular", "premium"] },
  { key: "pricingPage.fElev",     in: ["standard", "popular", "premium"] },
  { key: "pricingPage.f3d",       in: ["popular", "premium"] },
  { key: "pricingPage.fPlumb",    in: ["popular", "premium"] },
  { key: "pricingPage.fElec",     in: ["popular", "premium"] },
  { key: "pricingPage.fStruct",   in: ["premium"] },
  { key: "pricingPage.fSupport",  in: ["premium"] },
];

const GST_PERCENT = 18;
const fmtINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

export default function DesignCheckout() {
  const { t } = useI18n();
  const { tier: tierId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();

  const tier = DESIGN_TIERS[tierId] || DESIGN_TIERS.popular;

  const [form, setForm] = useState({
    name: user?.name || "",
    mobile: user?.mobile || "",
    email: user?.email || "",
    state: "",
    district: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const gst = useMemo(() => Math.floor((tier.price * GST_PERCENT) / 100), [tier.price]);
  const total = tier.price + gst;

  const validate = () => {
    if (!form.name.trim()) return "Please enter your full name.";
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, ""))) return "Enter a valid 10-digit mobile number.";
    return null;
  };

  const launchRazorpay = (orderResp) => new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error("Razorpay SDK not loaded. Please refresh and try again."));
      return;
    }
    const rzp = new window.Razorpay({
      key: orderResp.key,
      amount: orderResp.amount,
      currency: orderResp.currency,
      name: "NakshaCart",
      description: `${orderResp.tier_name} Design Package`,
      order_id: orderResp.razorpay_order_id,
      prefill: {
        name: orderResp.name,
        email: orderResp.email,
        contact: orderResp.contact,
      },
      notes: {
        internal_order_id: orderResp.order_id,
        design_tier: orderResp.tier,
      },
      theme: { color: "#10B981" },
      handler: (response) => resolve(response),
      modal: { ondismiss: () => reject(new Error("Payment cancelled.")) },
    });
    rzp.on("payment.failed", (resp) =>
      reject(new Error(resp?.error?.description || "Payment failed.")));
    rzp.open();
  });

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setSubmitting(true);
    try {
      const { data: orderResp } = await api.post("/checkout/design-tier/create", {
        tier: tier.id,
        billing_name: form.name.trim(),
        billing_mobile: form.mobile.replace(/\D/g, ""),
        billing_email: form.email.trim() || null,
        billing_state: form.state.trim() || null,
        billing_district: form.district.trim() || null,
      });

      const rzpResp = await launchRazorpay(orderResp);

      await api.post("/checkout/verify", {
        order_id: orderResp.order_id,
        razorpay_order_id: rzpResp.razorpay_order_id,
        razorpay_payment_id: rzpResp.razorpay_payment_id,
        razorpay_signature: rzpResp.razorpay_signature,
      });

      nav(`/order/success?order_id=${orderResp.order_id}&mobile=${encodeURIComponent(form.mobile)}`);
    } catch (err) {
      const apiDetail = err.response?.data?.detail;
      setError(apiDetail ? formatApiErrorDetail(apiDetail) : (err.message || "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-slate-50/50 min-h-screen" data-testid="design-checkout-page">
      <div className="bg-white border-b border-slate-200">
        <div className="nc-container py-3">
          <Link to="/cost-calculator" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700">
            <ArrowLeft className="w-4 h-4" /> Back to plans
          </Link>
        </div>
      </div>

      <div className="nc-container py-8 grid lg:grid-cols-12 gap-6">
        {/* Left: form */}
        <form onSubmit={submit} className="lg:col-span-7 space-y-4">
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
            Complete your purchase
          </h1>
          <p className="text-sm text-rose-600">{t("pricingPage.paymentNote")}</p>

          <div className="bg-white rounded-2xl border border-slate-200 p-5">
            <h2 className="font-semibold text-slate-900 text-sm uppercase tracking-wider mb-3">Your details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-slate-600">Full name *</label>
                <input className="nc-input mt-1" value={form.name} onChange={e => update("name", e.target.value)} data-testid="dc-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Mobile *</label>
                <input className="nc-input mt-1" maxLength={10}
                  value={form.mobile}
                  onChange={e => update("mobile", e.target.value.replace(/\D/g, ""))}
                  data-testid="dc-mobile" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <input type="email" className="nc-input mt-1" value={form.email} onChange={e => update("email", e.target.value)} data-testid="dc-email" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">State</label>
                <input className="nc-input mt-1" value={form.state} onChange={e => update("state", e.target.value)} data-testid="dc-state" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">District / City</label>
                <input className="nc-input mt-1" value={form.district} onChange={e => update("district", e.target.value)} data-testid="dc-district" />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700" data-testid="dc-error">{error}</div>
          )}

          <button type="submit" disabled={submitting}
            className="nc-btn-primary w-full justify-center text-base py-3"
            data-testid="dc-pay-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
            {submitting ? "Processing…" : `Pay ${fmtINR(total)}`}
          </button>
          <p className="text-xs text-slate-500 text-center">
            Secure payment via Razorpay. 100% refundable before design work starts.
          </p>
        </form>

        {/* Right: tier summary */}
        <div className="lg:col-span-5">
          <div className={`sticky top-20 bg-white rounded-2xl border-2 p-5 nc-soft-shadow ${tier.highlight ? "border-emerald-500" : "border-slate-200"}`}>
            <div className="flex items-baseline justify-between">
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t(tier.nameKey)}</h2>
              <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${tier.highlight ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                {t(tier.focusKey)}
              </span>
            </div>

            {tier.highlight && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                <Star className="w-3 h-3 fill-current" /> {t("pricingPage.mostPopular")}
              </div>
            )}

            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{fmtINR(tier.price)}</span>
              <span className="text-sm text-slate-400 line-through">{fmtINR(tier.mrp)}</span>
            </div>

            <ul className="mt-5 space-y-2 text-sm">
              {FEATURE_KEYS.map(f => {
                const on = f.in.includes(tier.id);
                return (
                  <li key={f.key} className="flex items-start gap-2">
                    {on
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                      : <XIcon className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />}
                    <span className={on ? "text-slate-700" : "text-slate-400 line-through"}>{t(f.key)}</span>
                  </li>
                );
              })}
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">{t("pricingPage.fValidity")}: {t(tier.validityKey)}</span>
              </li>
            </ul>

            <div className="mt-5 border-t border-slate-200 pt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Subtotal</span>
                <span className="font-medium text-slate-900">{fmtINR(tier.price)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">GST ({GST_PERCENT}%)</span>
                <span className="font-medium text-slate-900">{fmtINR(gst)}</span>
              </div>
              <div className="flex items-center justify-between text-base pt-2 border-t border-slate-100">
                <span className="font-semibold text-slate-900">Total</span>
                <span className="font-semibold text-slate-900">{fmtINR(total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
